// ─────────────────────────────────────────────────────────
// Einstellungen → Layout & Textvorlagen → Dateinamen (plancraft-Modell)
// ─────────────────────────────────────────────────────────
// Read-Karte (Elemente als Chips + Beispiel + „Bearbeiten") + Modal zum
// Bearbeiten (Elemente-Multiselect mit Chips/× + Element-Dropdown, Trennzeichen).
// Prefix fn-. Rechte: layout:read / layout:write.
// Sprache: die Bedien-Texte über t() — ABER die Musterwerte unten bleiben DEUTSCH.
import { icon, escapeHtml } from '../../utils/dom.js';
import { saveFileNaming } from '../../core/org.js';
import { t } from '../../i18n/index.js';

// Musterwerte für die Live-Vorschau (spiegelt server-seitiges buildExample).
// BEWUSST NICHT ÜBERSETZT: der echte Dateiname wird im Backend gebaut
// (file-naming.service.ts → typeLabel() liefert fest „Offerte"/„Rechnung", fmtDate
// fest DD.MM.YYYY). Die Datei geht als Anhang an den KUNDEN, nicht in die Oberfläche
// des Chefs — sie bleibt deutsch, egal welche UI-Sprache eingestellt ist.
// Würde man diese Vorschau übersetzen, zeigte sie einen Dateinamen, den es nie gibt.
const SAMPLE = {
  type: 'Offerte', number: 'DOC-123', subject: 'Meine Testangebot',
  projectName: 'Wohnung streichen', projectNumber: 'P-2026-001',
  recipient: 'Muster AG', date: '24.07.2026', dateIso: '2026-07-24',
};

// Server-Labels über den Key übersetzen, Server-Label als Fallback (wie docTypes.*).
const elLabel = (key, serverLabel) => {
  const k = 'settings.fileNaming.elements.' + key;
  const v = t(k);
  return v === k ? (serverLabel || key) : v;
};
// Nur „Leerzeichen" ist ein Wort; - und _ sind Zeichen und bleiben wie sie sind.
const sepLabel = (key, serverLabel) => (key === ' ' ? t('settings.fileNaming.sepSpace') : (serverLabel || key));

function data(s) {
  return s.fileNaming || {};
}
function cfg(s) {
  const d = data(s);
  return d.fileNaming || { elements: [], separator: '_' };
}
function labelMap(s) {
  const av = data(s).availableElements || [];
  const m = {};
  av.forEach((e) => { m[e.key] = elLabel(e.key, e.label); });
  return m;
}
function exampleFrom(conf) {
  const parts = (conf.elements || []).map((k) => SAMPLE[k]).filter(Boolean);
  return (parts.join(conf.separator || '_') || 'DOC-123') + '.pdf';
}

// ── Read-Ansicht ──────────────────────────────────────────
export function renderFileNamingSection(ctx) {
  const { s, can } = ctx;
  if (!can('layout:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  const c = cfg(s);
  const lm = labelMap(s);
  const sep = c.separator === ' ' ? '␣' : c.separator;
  const chips = (c.elements || []).map((k, i) => `
    ${i > 0 ? `<span class="cd-fn-sep">${escapeHtml(sep)}</span>` : ''}
    <span class="cd-fn-el">${escapeHtml(lm[k] || k)}</span>`).join('');
  const example = data(s).example || exampleFrom(c);
  const editBtn = can('layout:write') ? `<button class="cd-btn-ghost" data-action="fn-edit">${icon('edit', 15)}${escapeHtml(t('common.edit'))}</button>` : '';

  return `
    <div class="cd-emp">
      <div class="cd-emp-card">
        <div class="cd-emp-card-head">${icon('draft', 18)}<h3>${escapeHtml(t('settings.fileNaming.title'))}</h3>${editBtn ? `<div class="cd-fn-headbtn">${editBtn}</div>` : ''}</div>
        <p class="cd-sc-lead">${escapeHtml(t('settings.fileNaming.lead'))}</p>
        <div class="cd-fn-chips-view">${chips || `<span class="cd-muted">${escapeHtml(t('settings.fileNaming.noElements'))}</span>`}</div>
        <div class="cd-fn-example"><span class="cd-nr-cap">${escapeHtml(t('settings.fileNaming.example'))}</span> <code>${escapeHtml(example)}</code></div>
      </div>
    </div>`;
}

// ── Bearbeiten-Modal ──────────────────────────────────────
export function renderFileNamingModal(ctx) {
  const { s } = ctx;
  const e = s.fnEdit;
  if (!e) return '';
  const lm = labelMap(s);
  const available = data(s).availableElements || [];
  const separators = data(s).separators || [{ key: '-', label: '-' }, { key: '_', label: '_' }, { key: ' ', label: 'Leerzeichen' }];
  const chips = (e.elements || []).map((k, i) => `
    <span class="cd-fn-chip-edit">${escapeHtml(lm[k] || k)}
      <button class="cd-fn-chip-x" data-action="fn-del-el" data-index="${i}" title="${escapeHtml(t('settings.fileNaming.remove'))}">${icon('close', 14)}</button>
    </span>`).join('');
  const notAdded = available.filter((el) => !(e.elements || []).includes(el.key));
  const addOpts = [`<option value="">${escapeHtml(t('settings.fileNaming.addElement'))}</option>`]
    .concat(notAdded.map((el) => `<option value="${el.key}">${escapeHtml(elLabel(el.key, el.label))}</option>`)).join('');
  const sepOpts = separators.map((sp) => `<option value="${escapeHtml(sp.key)}" ${e.separator === sp.key ? 'selected' : ''}>${escapeHtml(sepLabel(sp.key, sp.label))}</option>`).join('');
  const example = exampleFrom(e);

  return `
    <div class="cd-emp-modal-overlay" data-action="fn-cancel">
      <div class="cd-emp-modal cd-fn-modal" data-action="fn-stop">
        <div class="cd-emp-modal-head">
          <h2>${icon('draft', 20)}${escapeHtml(t('settings.fileNaming.title'))}</h2>
          <div class="cd-set-head-actions">
            <button class="cd-btn-ghost" data-action="fn-cancel">${escapeHtml(t('common.cancel'))}</button>
            <button class="cd-btn-primary" data-action="fn-save" ${s.fnSaving ? 'disabled' : ''}>${escapeHtml(s.fnSaving ? t('common.saving') : t('common.save'))}</button>
          </div>
        </div>
        <div class="cd-emp-modal-body">
          <label class="cd-dt-cap">${escapeHtml(t('settings.fileNaming.elementsLabel'))}</label>
          <div class="cd-fn-edit-box">
            <div class="cd-fn-edit-chips">${chips}</div>
            ${notAdded.length ? `<div class="cd-select-shell cd-fn-add-shell"><select class="cd-input" id="fn-add-select">${addOpts}</select>${icon('expand_more', 18)}</div>` : ''}
          </div>
          <label class="cd-dt-cap" style="margin-top:14px;display:block;">${escapeHtml(t('settings.fileNaming.separator'))}</label>
          <div class="cd-select-shell"><select class="cd-input" id="fn-sep-select">${sepOpts}</select>${icon('expand_more', 18)}</div>
          <div class="cd-fn-example" style="margin-top:16px;"><span class="cd-nr-cap">${escapeHtml(t('settings.fileNaming.example'))}</span> <code>${escapeHtml(example)}</code></div>
        </div>
      </div>
    </div>`;
}

// ── Klick-Aktionen ────────────────────────────────────────
export function handleFileNamingAction(a, el, ctx) {
  const { s, draw, reload } = ctx;
  if (!a || a.indexOf('fn-') !== 0) return false;

  if (a === 'fn-stop') return true; // Klick im Modal: nicht schließen
  if (a === 'fn-edit') {
    if (!ctx.can('layout:write')) return true;
    const c = cfg(s);
    s.fnEdit = { elements: [...(c.elements || [])], separator: c.separator || '_' };
    draw();
    return true;
  }
  if (a === 'fn-cancel') { s.fnEdit = null; draw(); return true; }
  if (a === 'fn-del-el') {
    if (!s.fnEdit) return true;
    s.fnEdit.elements.splice(Number(el.dataset.index), 1);
    draw();
    return true;
  }
  if (a === 'fn-save') {
    if (!s.fnEdit || !ctx.can('layout:write')) return true;
    s.fnSaving = true; draw();
    saveFileNaming(s.fnEdit)
      .then(() => { s.fnSaving = false; s.fnEdit = null; if (reload) reload(); else draw(); })
      .catch((err) => { s.fnSaving = false; s.error = err.message || t('common.saveFailed'); draw(); });
    return true;
  }
  return false;
}

// ── Change-Events (Element-Dropdown + Trennzeichen) ───────
export function handleFileNamingChange(kind, value, ctx) {
  const { s, draw } = ctx;
  if (!s.fnEdit) return;
  if (kind === 'add') {
    if (value && !s.fnEdit.elements.includes(value)) s.fnEdit.elements.push(value);
    draw();
  } else if (kind === 'sep') {
    s.fnEdit.separator = value;
    draw();
  }
}
